/**
 * 练习引擎（Practice Engine）
 * ------------------------------------------------------------
 * 负责「一次练习会话」的纯逻辑（不含任何 DOM）：
 *   - 生成题目队列
 *   - 记录每题作答：对/错、是否用提示、耗时
 *   - 三级完成度判定：✓ 独立完成 / △ 提示后完成 / × 无法完成
 *   - 结算出统计对象，交给 records / rewards 落库
 *
 * UI 层（pages/session.js）只负责渲染和调用这里的 api，
 * 因此游戏、模拟卷、复习都可以复用同一套引擎。
 */
import { grade } from './graders/index.js';
import { generatorRegistry } from './generators/registry.js';
import { nowISO, uid } from '../utils/index.js';

/** 完成度判定 */
export const COMPLETION = { INDEPENDENT: 'independent', HINTED: 'hinted', FAILED: 'failed' };
export const COMPLETION_LABEL = { independent: '✓ 独立完成', hinted: '△ 提示后完成', failed: '× 无法完成' };

export class PracticeSession {
  /**
   * @param {object} cfg
   * @param {string} cfg.module    'word'|'sentence'|'grammar'|'comprehensive'|'review'
   * @param {object[]} cfg.questions 已经生成好的题目数组
   * @param {string} cfg.unitId
   * @param {string} cfg.textbookId
   * @param {string} cfg.title     显示用标题
   */
  constructor(cfg) {
    this.id = uid('ses');
    this.module = cfg.module || 'word';
    this.title = cfg.title || '';
    this.unitId = cfg.unitId || '';
    this.textbookId = cfg.textbookId || '';
    this.questions = cfg.questions || [];
    this.index = 0;
    this.records = [];           // 每题结果
    this.startedAt = nowISO();
    this.questionStartedAt = Date.now();
    this._hintUsed = false;
  }

  get total() { return this.questions.length; }
  get current() { return this.questions[this.index] || null; }
  get finished() { return this.index >= this.questions.length; }
  get progress() { return this.total ? this.index / this.total : 0; }

  /** 使用提示（会降低本题完成度为 △） */
  useHint() {
    this._hintUsed = true;
  }

  /**
   * 提交答案
   * @param {*} response 选择题为选项文本，填空为字符串（多空用 ||| 分隔），排序为数组
   * @param {object} [extra] { skipped:boolean }
   * @returns {{correct:boolean, partial:number, expected:string, detail:string, completion:string}}
   */
  submit(response, extra = {}) {
    const q = this.current;
    if (!q) return null;
    const graded = extra.skipped
      ? { correct: false, partial: 0, expected: q.answer?.[0] || '', detail: '已跳过' }
      : grade(q, response);

    const completion = graded.correct
      ? (this._hintUsed ? COMPLETION.HINTED : COMPLETION.INDEPENDENT)
      : COMPLETION.FAILED;

    const rec = {
      index: this.index,
      questionId: q.id,
      type: q.type,
      skill: q.skill,
      level: q.level,
      refType: q.refType || '',
      refId: q.refId || '',
      unitId: q.unitId || this.unitId,
      prompt: q.prompt,
      expected: graded.expected,
      response: Array.isArray(response) ? response.join('|') : String(response ?? ''),
      correct: !!graded.correct,
      partial: graded.partial ?? (graded.correct ? 1 : 0),
      hintUsed: this._hintUsed,
      skipped: !!extra.skipped,
      completion,
      costMs: Date.now() - this.questionStartedAt,
      answeredAt: nowISO(),
    };
    this.records.push(rec);

    this.index += 1;
    this.questionStartedAt = Date.now();
    this._hintUsed = false;
    return { ...graded, completion };
  }

  /** 结算统计（不含落库） */
  summary() {
    const total = this.records.length;
    const correct = this.records.filter(r => r.correct).length;
    const independent = this.records.filter(r => r.completion === COMPLETION.INDEPENDENT).length;
    const hinted = this.records.filter(r => r.completion === COMPLETION.HINTED).length;
    const failed = this.records.filter(r => r.completion === COMPLETION.FAILED).length;
    const bySkill = {};
    for (const r of this.records) {
      const k = r.skill || 'G';
      bySkill[k] = bySkill[k] || { total: 0, correct: 0 };
      bySkill[k].total += 1;
      if (r.correct) bySkill[k].correct += 1;
    }
    const byLevel = {};
    for (const r of this.records) {
      const k = String(r.level ?? 0);
      byLevel[k] = byLevel[k] || { total: 0, correct: 0 };
      byLevel[k].total += 1;
      if (r.correct) byLevel[k].correct += 1;
    }
    return {
      id: this.id,
      module: this.module,
      title: this.title,
      unitId: this.unitId,
      textbookId: this.textbookId,
      total,
      correct,
      independent,
      hinted,
      failed,
      accuracy: total ? Math.round((correct / total) * 100) : 0,
      score: total ? Math.round((correct / total) * 100) : 0,
      bySkill,
      byLevel,
      startedAt: this.startedAt,
      endedAt: nowISO(),
      durationMs: Date.now() - new Date(this.startedAt).getTime(),
      records: this.records,
    };
  }
}

/**
 * 便捷工厂：直接按生成器 id 出题并创建会话
 * @param {{generatorId:string, content:object, level?:number, count?:number, priority?:Set, unitId:string, textbookId:string, title?:string, module?:string, mix?:object, category?:string, mode?:string}} cfg
 */
export function createSessionFromGenerator(cfg) {
  const gen = generatorRegistry.get(cfg.generatorId);
  if (!gen) throw new Error(`未注册的生成器：${cfg.generatorId}`);
  const questions = gen.build({
    content: cfg.content,
    level: cfg.level,
    count: cfg.count,
    priority: cfg.priority,
    mix: cfg.mix,
    category: cfg.category,
    mode: cfg.mode,
  });
  return new PracticeSession({
    module: cfg.module || gen.module,
    questions,
    unitId: cfg.unitId,
    textbookId: cfg.textbookId,
    title: cfg.title || gen.title,
  });
}
